/**
 * Client-direct text translation through Google's free `translate_a/single`
 * endpoint (client=gtx), the same endpoint the SPA and the mobile app call.
 * It answers with `Access-Control-Allow-Origin: *`, so the browser calls it
 * with no proxy and each request leaves from the viewer's own IP.
 */

import { LOCAL_STORAGE, safeStorageGet, safeStorageSet } from './safe-storage.js';
import { createTimeoutSignal } from './timeout-signal.js';

const TRANSLATE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';

export const AUTO_LANGUAGE = 'auto';

/** Input is cut to this many code points. */
export const MAX_SOURCE_CHARS = 4000;

/** Google answers 400 to a GET URL past about 32k characters, so the URL-encoded text is capped too. */
const MAX_ENCODED_CHARS = 24000;

const MAX_ATTEMPTS = 2;
const ATTEMPT_TIMEOUT_MS = 4000;
const BASE_BACKOFF_MS = 250;
const CACHE_MAX_ENTRIES = 200;

const TARGET_STORAGE_KEY = 'ip.translation.target';

/**
 * Target languages offered in the panel select, as Google language codes.
 * Every site locale maps onto one of them (see localeToLanguage).
 */
export const TARGET_LANGUAGES = [
    'af', 'sq', 'am', 'ar', 'hy', 'az', 'eu', 'be', 'bn', 'bs', 'bg', 'ca', 'ceb', 'zh-CN', 'zh-TW',
    'hr', 'cs', 'da', 'nl', 'en', 'eo', 'et', 'tl', 'fi', 'fr', 'gl', 'ka', 'de', 'el', 'gu', 'ht',
    'ha', 'iw', 'hi', 'hu', 'is', 'ig', 'id', 'ga', 'it', 'ja', 'jw', 'kn', 'kk', 'km', 'ko', 'ku',
    'ky', 'lo', 'la', 'lv', 'lt', 'lb', 'mk', 'mg', 'ms', 'ml', 'mt', 'mi', 'mr', 'mn', 'my', 'ne',
    'no', 'ps', 'fa', 'pl', 'pt', 'pa', 'ro', 'ru', 'sm', 'sr', 'si', 'sk', 'sl', 'so', 'es', 'sw',
    'sv', 'tg', 'ta', 'te', 'th', 'tr', 'uk', 'ur', 'uz', 'vi', 'cy', 'xh', 'yi', 'yo', 'zu',
];

export class TranslationError extends Error {
    /**
     * @param {string} message
     * @param {boolean} retryable - a network error, a timeout, a 429 or a 5xx
     */
    constructor(message, retryable = false) {
        super(message);
        this.name = 'TranslationError';
        this.retryable = retryable;
    }
}

/**
 * Cut text to MAX_SOURCE_CHARS code points and MAX_ENCODED_CHARS encoded
 * characters. Array.from splits by code point, so the cut never leaves half
 * of a surrogate pair.
 *
 * @param {string} text
 * @returns {{text: string, truncated: boolean}}
 */
export function truncateSource(text) {
    const chars = Array.from(text);
    let encoded = 0;
    let end = 0;
    while (end < chars.length && end < MAX_SOURCE_CHARS) {
        encoded += encodeURIComponent(chars[end]).length;
        if (encoded > MAX_ENCODED_CHARS) {
            break;
        }
        end += 1;
    }
    if (end === chars.length) {
        return { text, truncated: false };
    }

    return { text: chars.slice(0, end).join(''), truncated: true };
}

export function buildTranslateUrl(text, from, to) {
    const params = new URLSearchParams({ client: 'gtx', dt: 't', sl: from, tl: to, q: text });

    return `${TRANSLATE_ENDPOINT}?${params}`;
}

/**
 * Response shape: `[[["translated", "source", ...], ...], null, "<detected source>", ...]`.
 *
 * @returns {{text: string, detectedSource: string}} detectedSource is '' when Google omits it
 */
export function parseTranslateResponse(data) {
    if (!Array.isArray(data) || !Array.isArray(data[0])) {
        throw new TranslationError('Unexpected translation response shape');
    }
    const text = data[0]
        .map((segment) => (Array.isArray(segment) && typeof segment[0] === 'string' ? segment[0] : ''))
        .join('');
    if (!text.trim()) {
        throw new TranslationError('Empty translation response');
    }
    const detectedSource = typeof data[2] === 'string' ? data[2] : '';

    return { text, detectedSource };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchOnce(url) {
    const timeout = createTimeoutSignal(ATTEMPT_TIMEOUT_MS);
    try {
        let response;
        try {
            response = await fetch(url, { signal: timeout.signal });
        } catch (error) {
            throw new TranslationError(error instanceof Error ? error.message : 'Translation request failed', true);
        }
        if (!response.ok) {
            const status = response.status;
            throw new TranslationError(`Translation request failed (${status})`, status === 429 || status >= 500);
        }
        try {
            return await response.json();
        } catch (error) {
            // A stalled or cut body is a transport failure. Only malformed JSON is final.
            const malformed = error instanceof SyntaxError;
            throw new TranslationError(malformed ? 'Translation response is not JSON' : 'Translation response did not arrive', !malformed);
        }
    } finally {
        timeout.clear();
    }
}

async function requestWithRetry(text, from, to) {
    const url = buildTranslateUrl(text, from, to);
    for (let attempt = 0; ; attempt += 1) {
        try {
            return parseTranslateResponse(await fetchOnce(url));
        } catch (error) {
            if (!(error instanceof TranslationError) || !error.retryable || attempt + 1 >= MAX_ATTEMPTS) {
                throw error;
            }
        }
        await sleep(BASE_BACKOFF_MS * 2 ** attempt + Math.random() * BASE_BACKOFF_MS);
    }
}

/** In-memory only: message text is private, so nothing goes to storage. */
const cache = new Map();

function cacheKey(text, from, to) {
    return `${from}>${to}:${text}`;
}

function remember(key, value) {
    cache.delete(key);
    cache.set(key, value);
    while (cache.size > CACHE_MAX_ENTRIES) {
        cache.delete(cache.keys().next().value);
    }
}

/**
 * Translate text into `to`, with source-language detection unless `from` is set.
 * Rejects with a TranslationError.
 *
 * @param {string} text
 * @param {{from?: string, to: string}} options
 * @returns {Promise<{text: string, detectedSource: string, truncated: boolean}>}
 */
export async function translateText(text, { from = AUTO_LANGUAGE, to }) {
    const source = truncateSource(text.trim());
    if (!source.text) {
        throw new TranslationError('Nothing to translate');
    }
    const key = cacheKey(source.text, from, to);
    const cached = cache.get(key);
    if (cached) {
        return { ...cached, truncated: source.truncated };
    }
    const result = await requestWithRetry(source.text, from, to);
    remember(key, result);

    return { ...result, truncated: source.truncated };
}

/**
 * The Google code for a site locale such as `en-US`, `zh-TW` or `es-419`.
 * Chinese keeps its script, every other locale keeps its language only.
 *
 * @param {string} locale
 * @returns {string}
 */
export function localeToLanguage(locale) {
    const [language = 'en', region = ''] = String(locale || 'en').replace('_', '-').split('-');
    const lower = language.toLowerCase();
    if (lower === 'zh') {
        return ['TW', 'HK', 'MO'].includes(region.toUpperCase()) ? 'zh-TW' : 'zh-CN';
    }
    if (lower === 'he') {
        return 'iw';
    }

    return lower || 'en';
}

/** The viewer's saved target language, else the page language. */
export function getTargetLanguage() {
    const saved = safeStorageGet(TARGET_STORAGE_KEY, LOCAL_STORAGE);

    return saved && TARGET_LANGUAGES.includes(saved) ? saved : localeToLanguage(document.documentElement.lang);
}

export function saveTargetLanguage(language) {
    safeStorageSet(TARGET_STORAGE_KEY, language, LOCAL_STORAGE);
}

/** Google codes that differ from the BCP 47 code Intl.DisplayNames expects. */
const BCP47_ALIASES = { iw: 'he', jw: 'jv' };

const displayNames = new Map();

/**
 * The name of a Google language code in the page language, or the code itself.
 *
 * @param {string} code
 * @returns {string}
 */
export function languageName(code) {
    const locale = document.documentElement.lang || 'en';
    try {
        if (!displayNames.has(locale)) {
            displayNames.set(locale, new Intl.DisplayNames([locale], { type: 'language' }));
        }
        return displayNames.get(locale).of(BCP47_ALIASES[code] ?? code) || code;
    } catch {
        return code;
    }
}

/**
 * The target languages with names in the page language, sorted by name.
 *
 * @returns {Array<{code: string, name: string}>}
 */
export function targetLanguageOptions() {
    const uiLocale = document.documentElement.lang || 'en';

    return TARGET_LANGUAGES
        .map((code) => ({ code, name: languageName(code) }))
        .sort((a, b) => a.name.localeCompare(b.name, uiLocale));
}
